import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { API, auth, money, fmtDate, exportExcel, printArea, PrintStyle, exportPdf, inRange } from '../shared';
import KpiCard from '../KpiCard';
import SimpleBarList from '../SimpleBarList';
import ReportActions from '../ReportActions';
import { SectionCard, EmptyState } from '../../../../components/dashboard/TodaySchedule';
import { getReportSettings, logExport } from '../../../../lib/reportStudioApi';
import { useClubIdentity } from '../../../../lib/useClubIdentity';
import ReportPrintFrame from '../../../../components/reportStudio/ReportPrintFrame';

const EXPENSE_CATS = { salaries: 'رواتب', maintenance: 'صيانة', utilities: 'مرافق', equipment: 'معدات', marketing: 'تسويق', transportation: 'مواصلات', food: 'إعاشة', medical: 'طبي', training: 'تدريب', other: 'أخرى' };
const REVENUE_CATS = { membership: 'اشتراكات', sponsorship: 'رعاية', tickets: 'تذاكر', merchandise: 'مبيعات', training_fees: 'رسوم تدريب', donations: 'تبرعات', other: 'أخرى' };

const getUserName = () => { try { return JSON.parse(localStorage.getItem('user') || 'null')?.full_name; } catch { return null; } };

export default function FinancialTab({ filters, seasonId }) {
  const [loading, setLoading] = useState(true);
  const [expenses, setExpenses] = useState([]);
  const [revenues, setRevenues] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [studio, setStudio] = useState(null);
  const identity = useClubIdentity();
  const printRef = useRef(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const qs = seasonId && seasonId !== 'all' ? `season_id=${seasonId}` : '';
      const g = (url) => axios.get(`${API}${url}`, auth()).then((r) => r.data).catch(() => null);
      const [e, r, i, st] = await Promise.all([
        g(`/expenses${qs ? `?${qs}` : ''}`), g(`/revenues${qs ? `?${qs}` : ''}`), g(`/club-invoices?limit=200${qs ? `&${qs}` : ''}`), getReportSettings('financial_summary').catch(() => null),
      ]);
      setExpenses(Array.isArray(e) ? e : []);
      setRevenues(Array.isArray(r) ? r : []);
      setInvoices(i?.items || []);
      setStudio(st);
      setLoading(false);
    })();
  }, [seasonId]);

  if (loading) {
    return <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">{[1, 2, 3, 4].map((i) => <div key={i} className="h-24 animate-pulse rounded-xl bg-slate-100" />)}</div>;
  }

  const settings = studio?.effective || {};
  const content = settings.content || {};
  const templateUsed = studio?.use_custom ? 'تخصيص مستقل' : null;

  const exp = expenses.filter((x) => x.status !== 'rejected' && inRange(x.expense_date, filters.date_from, filters.date_to));
  const rev = revenues.filter((x) => inRange(x.revenue_date, filters.date_from, filters.date_to));
  const totalExp = exp.reduce((s, x) => s + x.amount, 0);
  const totalRev = rev.reduce((s, x) => s + x.amount, 0);
  const receivables = invoices.filter((i) => ['unpaid', 'partial'].includes(i.status));
  const receivablesTotal = receivables.reduce((s, i) => s + (i.total - (i.paid_amount || 0)), 0);

  const expByCat = {};
  exp.forEach((x) => { expByCat[x.category] = (expByCat[x.category] || 0) + x.amount; });
  const revByCat = {};
  rev.forEach((x) => { revByCat[x.category] = (revByCat[x.category] || 0) + x.amount; });

  const rows = [
    ...rev.map((x) => ({ 'النوع': 'إيراد', 'البيان': x.title, 'التصنيف': REVENUE_CATS[x.category] || x.category, 'التاريخ': x.revenue_date, 'المبلغ': x.amount })),
    ...exp.map((x) => ({ 'النوع': 'مصروف', 'البيان': x.title, 'التصنيف': EXPENSE_CATS[x.category] || x.category, 'التاريخ': x.expense_date, 'المبلغ': -x.amount })),
  ];

  const handlePdf = async () => {
    try {
      await exportPdf(printRef.current, 'التقرير-المالي', { orientation: settings.page?.orientation, paperSize: settings.page?.paper_size });
      logExport('financial_summary', 'pdf', templateUsed, 'success', 'التقرير-المالي.pdf');
    } catch { logExport('financial_summary', 'pdf', templateUsed, 'failed', null); }
  };
  const handleExcel = () => {
    exportExcel(rows, 'تقرير-مالي');
    logExport('financial_summary', 'excel', templateUsed, 'success', 'تقرير-مالي.xlsx');
  };

  return (
    <div className="space-y-4" data-testid="financial-tab">
      <PrintStyle />
      <div className="flex justify-end print:hidden">
        <ReportActions testId="financial-report" onExcel={handleExcel} onPdf={handlePdf} onPrint={printArea} disabled={rows.length === 0} />
      </div>
      <div className="print-area" ref={printRef}>
        <ReportPrintFrame settings={settings} identity={identity} reportTitle="التقرير المالي" exportedByName={getUserName()}>
          <div className="space-y-4">
            {content.stat_cards !== false && (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <KpiCard icon="arrowUp" label="الإيرادات" value={money(totalRev)} accent="text-teal-700" testId="kpi-fin-rev" />
                <KpiCard icon="arrowDown" label="المصروفات" value={money(totalExp)} accent="text-red-600" testId="kpi-fin-exp" />
                <KpiCard icon="banknotes" label="الصافي" value={money(totalRev - totalExp)} testId="kpi-fin-net" />
                <KpiCard icon="document" label="مستحقات غير محصلة" value={money(receivablesTotal)} accent="text-amber-600" testId="kpi-fin-receivables" />
              </div>
            )}

            {content.charts !== false && (
              <div className="grid gap-4 sm:grid-cols-2">
                <SectionCard title="توزيع المصروفات" icon="arrowDown" testId="financial-exp-card">
                  {Object.keys(expByCat).length === 0
                    ? <EmptyState icon="banknotes" text="لا توجد مصروفات في الفترة" testId="financial-exp-empty" />
                    : <SimpleBarList items={Object.entries(expByCat).map(([k, v]) => ({ label: EXPENSE_CATS[k] || k, value: Math.round(v) }))} testId="financial-exp-bars" />}
                </SectionCard>
                <SectionCard title="توزيع الإيرادات" icon="arrowUp" testId="financial-rev-card">
                  {Object.keys(revByCat).length === 0
                    ? <EmptyState icon="banknotes" text="لا توجد إيرادات في الفترة" testId="financial-rev-empty" />
                    : <SimpleBarList items={Object.entries(revByCat).map(([k, v]) => ({ label: REVENUE_CATS[k] || k, value: Math.round(v) }))} testId="financial-rev-bars" />}
                </SectionCard>
              </div>
            )}

            {content.tables !== false && (
              <SectionCard title="حركات الفترة" icon="document" testId="financial-rows-card">
                {rows.length === 0 ? <EmptyState icon="banknotes" text="لا توجد عمليات في الفترة المحددة" testId="financial-rows-empty" /> : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead><tr className="border-b border-slate-100 text-right text-xs text-slate-500"><th className="px-3 py-2">النوع</th><th className="px-3 py-2">البيان</th><th className="px-3 py-2">التاريخ</th><th className="px-3 py-2">المبلغ</th></tr></thead>
                      <tbody>
                        {rows.slice(0, 200).map((r, idx) => (
                          <tr key={idx} className="border-b border-slate-50" data-testid="financial-row">
                            <td className="px-3 py-2"><span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${r['المبلغ'] >= 0 ? 'bg-teal-50 text-teal-700' : 'bg-red-50 text-red-600'}`}>{r['النوع']}</span></td>
                            <td className="px-3 py-2 text-slate-800">{r['البيان']}</td>
                            <td className="px-3 py-2 text-slate-600">{fmtDate(r['التاريخ'])}</td>
                            <td className={`px-3 py-2 font-bold ${r['المبلغ'] >= 0 ? 'text-teal-700' : 'text-red-600'}`} dir="rtl">{money(Math.abs(r['المبلغ']))}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </SectionCard>
            )}
          </div>
        </ReportPrintFrame>
      </div>
    </div>
  );
}
